"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.MembershipService = void 0;
const constants_1 = require("../constants");
const uuid_1 = require("uuid");
const crypto_1 = require("@akord/crypto");
const service_1 = require("./service");
const membership_1 = require("../types/membership");
const contract_1 = require("../types/contract");
const bad_request_1 = require("../errors/bad-request");
const incorrect_encryption_key_1 = require("../errors/incorrect-encryption-key");
const common_1 = require("./common");
const profile_1 = require("./profile");
class MembershipService extends service_1.Service {
    constructor() {
        super(...arguments);
        this.objectType = constants_1.objectType.MEMBERSHIP;
        this.defaultListOptions = {
            shouldDecrypt: true,
            filter: {
                or: [
                    { status: { eq: constants_1.status.ACCEPTED } },
                    { status: { eq: constants_1.status.PENDING } }
                ]
            }
        };
        this.defaultGetOptions = {
            shouldDecrypt: true,
        };
    }
    /**
     * @param  {string} membershipId
     * @returns Promise with the decrypted membership
     */
    get(membershipId, options = this.defaultGetOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const getOptions = Object.assign(Object.assign({}, this.defaultGetOptions), options);
            const membershipProto = yield this.api.getMembership(membershipId, getOptions.vaultId);
            return new membership_1.Membership(membershipProto, membershipProto.__keys__);
        });
    }
    /**
     * @param  {string} vaultId
     * @param  {ListOptions} options
     * @returns Promise with paginated memberships within given vault
     */
    list(vaultId, options = this.defaultListOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const listOptions = Object.assign(Object.assign({}, this.defaultListOptions), options);
            const { items, nextToken } = yield this.api.getMembershipsByVaultId(vaultId, listOptions);
            return {
                items: items.map((memProto) => new membership_1.Membership(memProto)),
                nextToken: nextToken
            };
        });
    }
    /**
     * @param  {string} vaultId
     * @param  {ListOptions} options
     * @returns Promise with all memberships within given vault
     */
    listAll(vaultId, options = this.defaultListOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            const list = (options) => __awaiter(this, void 0, void 0, function* () {
                return yield this.list(options.vaultId, options);
            });
            return yield (0, common_1.paginate)(list, Object.assign(Object.assign({}, options), { vaultId }));
        });
    }
    /**
     * Invite user with an Akord account
     * @param  {string} vaultId
     * @param  {string} email invitee's email
     * @param  {RoleType} role VIEWER/CONTRIBUTOR/OWNER
     * @param  {MembershipCreateOptions} [options] invitation email message, etc.
     * @returns Promise with new membership id & corresponding transaction id
     */
    invite(vaultId, email, role, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.setVaultContext(vaultId);
            this.setActionRef(constants_1.actionRefs.MEMBERSHIP_INVITE);
            this.setFunction(constants_1.functions.MEMBERSHIP_INVITE);
            const membershipId = (0, uuid_1.v4)();
            this.setObjectId(membershipId);
            const { address, publicKey, publicSigningKey } = yield this.api.getUserPublicData(email);
            const state = {
                keys: yield this.prepareMemberKeys(publicKey),
                encPublicSigningKey: yield this.processWriteString(publicSigningKey)
            };
            this.arweaveTags = [new contract_1.Tag(constants_1.protocolTags.MEMBER_ADDRESS, address)]
                .concat(yield this.getTxTags());
            const dataTxId = yield this.uploadState(state, this.vault.cloud);
            const input = {
                function: this.function,
                address,
                role,
                data: dataTxId
            };
            const { id, object } = yield this.api.postContractTransaction(this.vaultId, input, this.arweaveTags, { message: options.message });
            return { membershipId, transactionId: id, object: object };
        });
    }
    /**
     * Airdrop access to the vault directly through public keys
     * @param  {string} vaultId
     * @param  {{publicKey:string,publicSigningKey:string,role:RoleType}[]} members
     * @returns Promise with new memberships & corresponding transaction id
     */
    airdrop(vaultId, members) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.setVaultContext(vaultId);
            this.setActionRef("MEMBERSHIP_AIRDROP");
            this.setFunction(constants_1.functions.MEMBERSHIP_ADD);
            const memberArray = [];
            const membersMetadata = [];
            const dataArray = [];
            const memberTags = [];
            for (const member of members) {
                const membershipId = (0, uuid_1.v4)();
                this.setObjectId(membershipId);
                const memberAddress = yield (0, crypto_1.deriveAddress)((0, crypto_1.base64ToArray)(member.publicSigningKey));
                const state = {
                    id: membershipId,
                    address: memberAddress,
                    keys: yield this.prepareMemberKeys(member.publicKey),
                    encPublicSigningKey: yield this.processWriteString(member.publicSigningKey),
                    //    memberDetails: { name: member.options?.name },
                };
                const data = yield this.uploadState(state, this.vault.cloud);
                dataArray.push({
                    id: membershipId,
                    data
                });
                membersMetadata.push(Object.assign({ address: memberAddress, publicKey: member.publicKey, publicSigningKey: member.publicSigningKey }, member.options));
                memberArray.push({ address: memberAddress, id: membershipId, role: member.role, data });
                memberTags.push(new contract_1.Tag(constants_1.protocolTags.MEMBER_ADDRESS, memberAddress));
                memberTags.push(new contract_1.Tag(constants_1.protocolTags.MEMBERSHIP_ID, membershipId));
            }
            this.arweaveTags = memberTags.concat(yield this.getTxTags());
            const input = {
                function: this.function,
                members: memberArray
            };
            const { id } = yield this.api.postContractTransaction(this.vaultId, input, this.arweaveTags, { members: membersMetadata });
            return { members: input.members, transactionId: id };
        });
    }
    /**
     * @param  {string} membershipId
     * @returns Promise with corresponding transaction id
     */
    accept(membershipId) {
        return __awaiter(this, void 0, void 0, function* () {
            const profileService = new profile_1.ProfileService(this.wallet, this.api);
            const memberDetails = yield profileService.get();
            yield this.setVaultContextFromMembershipId(membershipId);
            const state = {
                //  memberDetails: memberDetails,
                encPublicSigningKey: yield this.processWriteString(this.wallet.signingPublicKey())
            };
            this.setActionRef(constants_1.actionRefs.MEMBERSHIP_ACCEPT);
            this.setFunction(constants_1.functions.MEMBERSHIP_ACCEPT);
            const data = yield this.mergeAndUploadState(state, this.vault.cloud);
            const { id, object } = yield this.api.postContractTransaction(this.vaultId, { function: this.function, data }, yield this.getTxTags());
            return { transactionId: id, object: new membership_1.Membership(object) };
        });
    }
    /**
     * @param  {string} membershipId
     * @returns Promise with corresponding transaction id
     */
    confirm(membershipId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.setVaultContextFromMembershipId(membershipId);
            this.setActionRef(constants_1.actionRefs.MEMBERSHIP_CONFIRM);
            this.setFunction(constants_1.functions.MEMBERSHIP_INVITE);
            const { address, publicKey, publicSigningKey } = yield this.api.getUserPublicData(this.object.email);
            const state = {
                keys: yield this.prepareMemberKeys(publicKey),
                encPublicSigningKey: yield this.processWriteString(publicSigningKey)
            };
            this.arweaveTags = [new contract_1.Tag(constants_1.protocolTags.MEMBER_ADDRESS, address)]
                .concat(yield this.getTxTags());
            const dataTxId = yield this.uploadState(state, this.vault.cloud);
            const input = {
                function: this.function,
                address,
                data: dataTxId,
                role: this.object.role
            };
            const { id, object } = yield this.api.postContractTransaction(this.vaultId, input, this.arweaveTags);
            return { transactionId: id, object: new membership_1.Membership(object) };
        });
    }
    /**
     * @param  {string} membershipId
     * @returns Promise with corresponding transaction id
     */
    reject(membershipId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.setVaultContextFromMembershipId(membershipId);
            this.setActionRef(constants_1.actionRefs.MEMBERSHIP_REJECT);
            this.setFunction(constants_1.functions.MEMBERSHIP_REJECT);
            const { id, object } = yield this.api.postContractTransaction(this.vaultId, { function: this.function }, yield this.getTxTags());
            return { transactionId: id, object: new membership_1.Membership(object) };
        });
    }
    /**
     * @param  {string} membershipId
     * @returns Promise with corresponding transaction id
     */
    leave(membershipId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.setVaultContextFromMembershipId(membershipId);
            this.setActionRef(constants_1.actionRefs.MEMBERSHIP_LEAVE);
            this.setFunction(constants_1.functions.MEMBERSHIP_REJECT);
            const { id, object } = yield this.api.postContractTransaction(this.vaultId, { function: this.function }, yield this.getTxTags());
            return { transactionId: id, object: new membership_1.Membership(object) };
        });
    }
    /**
     * @param  {string} membershipId
     * @returns Promise with corresponding transaction id
     */
    revoke(membershipId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.setVaultContextFromMembershipId(membershipId);
            this.setActionRef(constants_1.actionRefs.MEMBERSHIP_REVOKE);
            this.setFunction(constants_1.functions.MEMBERSHIP_REVOKE);
            this.arweaveTags = yield this.getTxTags();
            let data;
            if (!this.isPublic) {
                const memberships = yield this.listAll(this.vaultId, { shouldDecrypt: false });
                const activeMembers = memberships.filter((member) => member.id !== this.objectId
                    && (member.status === constants_1.status.ACCEPTED || member.status === constants_1.status.PENDING));
                // rotate keys for all active members
                const memberPublicKeys = new Map();
                yield Promise.all(activeMembers.map((member) => __awaiter(this, void 0, void 0, function* () {
                    const { publicKey } = yield this.api.getUserPublicData(member.email);
                    memberPublicKeys.set(member.id, publicKey);
                })));
                const { memberKeys } = yield this.rotateMemberKeys(memberPublicKeys);
                // upload new state for all active members
                data = [];
                yield Promise.all(activeMembers.map((member) => __awaiter(this, void 0, void 0, function* () {
                    const memberService = new MembershipService(this.wallet, this.api);
                    memberService.setVaultId(this.vaultId);
                    memberService.setObjectId(member.id);
                    memberService.setObject(member);
                    const dataTx = yield memberService.mergeAndUploadState({ keys: memberKeys.get(member.id) }, this.vault.cloud);
                    data.push({ id: member.id, value: dataTx });
                })));
            }
            const { id, object } = yield this.api.postContractTransaction(this.vaultId, { function: this.function, data }, this.arweaveTags);
            return { transactionId: id, object: new membership_1.Membership(object) };
        });
    }
    /**
     * @param  {string} membershipId
     * @param  {RoleType} role VIEWER/CONTRIBUTOR/OWNER
     * @returns Promise with corresponding transaction id
     */
    changeRole(membershipId, role) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.setVaultContextFromMembershipId(membershipId);
            this.setActionRef(constants_1.actionRefs.MEMBERSHIP_CHANGE_ROLE);
            this.setFunction(constants_1.functions.MEMBERSHIP_CHANGE_ROLE);
            const { id, object } = yield this.api.postContractTransaction(this.vaultId, { function: this.function, role }, yield this.getTxTags());
            return { transactionId: id, object: new membership_1.Membership(object) };
        });
    }
    /**
     * Invite user without an Akord account
     * @param  {string} vaultId
     * @param  {string} email invitee's email
     * @param  {string} role CONTRIBUTOR or VIEWER
     * @param  {MembershipCreateOptions} [options] invitation email message, etc.
     * @returns Promise with new membership id & corresponding transaction id
     */
    inviteNewUser(vaultId, email, role, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { id } = yield this.api.inviteNewUser(vaultId, email, role, options.message);
            return { membershipId: id };
        });
    }
    /**
   * Revoke invite for user without an Akord account
   * @param  {string} vaultId
   * @param  {string} membershipId
   */
    revokeInvite(vaultId, membershipId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.api.revokeInvite(vaultId, membershipId);
        });
    }
    /**
     * @param  {string} membershipId
     * @returns Promise with corresponding transaction id
     */
    inviteResend(membershipId) {
        return __awaiter(this, void 0, void 0, function* () {
            const membership = yield this.api.getMembership(membershipId);
            if (membership.status !== constants_1.status.PENDING && membership.status !== constants_1.status.INVITED) {
                throw new bad_request_1.BadRequest("Cannot resend the invitation for member: " + membershipId +
                    ". Found invalid status: " + membership.status);
            }
            yield this.api.inviteResend(membership.vaultId, membershipId);
        });
    }
    setVaultContextFromMembershipId(membershipId, vaultId) {
        return __awaiter(this, void 0, void 0, function* () {
            const membership = yield this.api.getMembership(membershipId, vaultId);
            const vault = yield this.api.getVault(membership.vaultId);
            this.setVault(vault);
            this.setVaultId(membership.vaultId);
            this.setIsPublic(membership.__public__);
            yield this.setMembershipKeys(membership);
            this.setObject(membership);
            this.setObjectId(membershipId);
            this.setObjectType(this.objectType);
        });
    }
    getTxTags() {
        const _super = Object.create(null, {
            getTxTags: { get: () => super.getTxTags }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const tags = yield _super.getTxTags.call(this);
            return tags.concat(new contract_1.Tag(constants_1.protocolTags.MEMBERSHIP_ID, this.objectId));
        });
    }
    prepareMemberKeys(publicKey) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isPublic) {
                const keysEncrypter = new crypto_1.Encrypter(this.wallet, this.dataEncrypter.keys, (0, crypto_1.base64ToArray)(publicKey));
                try {
                    const keys = yield keysEncrypter.encryptMemberKeys([]);
                    return keys.map((keyPair) => {
                        delete keyPair.publicKey;
                        return keyPair;
                    });
                }
                catch (error) {
                    throw new incorrect_encryption_key_1.IncorrectEncryptionKey(error);
                }
            }
            else {
                return null;
            }
        });
    }
    rotateMemberKeys(publicKeys) {
        return __awaiter(this, void 0, void 0, function* () {
            const memberKeys = new Map();
            // generate a new vault key pair
            const keyPair = yield (0, crypto_1.generateKeyPair)();
            for (let [memberId, publicKey] of publicKeys) {
                const memberKeysEncrypter = new crypto_1.Encrypter(this.wallet, this.dataEncrypter.keys, (0, crypto_1.base64ToArray)(publicKey));
                try {
                    memberKeys.set(memberId, [yield memberKeysEncrypter.encryptMemberKey(keyPair)]);
                }
                catch (error) {
                    throw new incorrect_encryption_key_1.IncorrectEncryptionKey(error);
                }
            }
            return { memberKeys, keyPair };
        });
    }
}
exports.MembershipService = MembershipService;
;
